import { FC, useMemo } from 'react';
import { NodeProps, Position, useNodeConnections } from '@xyflow/react';
import { TextEllipsis } from '../../../../common/components';
import { FormSelectSelectionLabel } from '../../../../common/form/input/FormSelectSelectionLabel';
import { formatCodeName } from '../../../../common/utils';
import { AlgorithmGraphReadOnlyField } from '../../AlgorithmGraphReadOnlyField';
import { GraphCard } from '../../GraphCard';
import { GraphHandle } from '../../GraphHandle';
import { getAlgorithmSourceLabel } from '../../utils';
import { AlgorithmTargetMapTypeSourceField } from '../AlgorithmTargetMapTypeSourceField';
import { useAlgorithmTargetRuleOptions } from '../ruleOptions/useAlgorithmTargetRuleOptions';
import { decodeRuleSourceItemValue } from '../utils';
import { AlgorithmTargetMappingGraphStore } from './AlgorithmTargetMappingGraphStore';
import { AlgorithmTargetMappingNode } from './types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmTargetMappingsGraphSourceNode.translations.json';
import { DataObjectDescriptionVersionStore } from '../../../DataObjectDescriptionVersionStore';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-target-mappings-graph-source-node',
);
addTranslations(translations);

export const AlgorithmTargetMappingsGraphSourceNode: FC<
  NodeProps<AlgorithmTargetMappingNode>
> = (props) => {
  const [readOnly] = AlgorithmTargetMappingGraphStore.useValue('readOnly');
  const connections = useNodeConnections({ handleType: 'source' });

  return (
    <GraphCard size="small">
      <GraphHandle
        type="source"
        position={Position.Right}
        isConnectable={!connections.length}
      />
      {readOnly ? <ReadOnly {...props} /> : <Editable {...props} />}
    </GraphCard>
  );
};

const ReadOnly: FC<NodeProps<AlgorithmTargetMappingNode>> = (props) => {
  const { sourceValue } = props.data;
  const [isLoading] = AlgorithmTargetMappingGraphStore.useValue('isLoading');
  const { t } = useTranslation();

  const item = decodeRuleSourceItemValue(sourceValue);

  return (
    <AlgorithmGraphReadOnlyField label={t('label')} isLoading={isLoading}>
      {'algorithmSourceId' in item ? (
        <ReadOnlyAlgorithmSource {...item} />
      ) : 'algorithmMappingId' in item ? (
        <ReadOnlyAlgorithmMapping {...item} />
      ) : (
        sourceValue
      )}
    </AlgorithmGraphReadOnlyField>
  );
};

const ReadOnlyAlgorithmSource: FC<{
  algorithmSourceId: number;
  sourceItemCode: string;
}> = (props) => {
  const { algorithmSourceId, sourceItemCode } = props;
  const [sources] = AlgorithmTargetMappingGraphStore.useValue('sources');

  const [algorithmSourceLabel, label] = useMemo(() => {
    const source = sources.find((s) => s.id === algorithmSourceId);

    if (!source) {
      return [undefined, undefined];
    }

    return [
      getAlgorithmSourceLabel(source),
      formatCodeName(
        ([] as { code: string; name: string }[])
          .concat(source.dimensions, source.indicators)
          ?.find((d) => d.code === sourceItemCode),
      ) || sourceItemCode,
    ];
  }, [algorithmSourceId, sourceItemCode, sources]);

  return (
    <FormSelectSelectionLabel ellipsis supertitle={algorithmSourceLabel}>
      {label}
    </FormSelectSelectionLabel>
  );
};

const ReadOnlyAlgorithmMapping: FC<{ algorithmMappingId: number }> = (
  props,
) => {
  const { algorithmMappingId } = props;
  const [mappings] = AlgorithmTargetMappingGraphStore.useValue('mappings');

  const mapping = useMemo(
    () => mappings.find((m) => m.id === algorithmMappingId),
    [algorithmMappingId, mappings],
  );

  return <TextEllipsis>{mapping?.alias}</TextEllipsis>;
};

const Editable: FC<NodeProps<AlgorithmTargetMappingNode>> = (props) => {
  const { index, sourceValue, targetValue } = props.data;
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const [algorithmTypeCode] =
    AlgorithmTargetMappingGraphStore.useValue('algorithmTypeCode');
  const [sources] = AlgorithmTargetMappingGraphStore.useValue('sources');
  const [mappings] = AlgorithmTargetMappingGraphStore.useValue('mappings');
  const areDbSources = useMemo(
    () => sources.some((source) => source.sourceType === 'DB'),
    [sources],
  );
  const { sourceOptions } = useAlgorithmTargetRuleOptions({
    algorithmTypeCode,
    dataObjectVersion: dataObjectDescriptionVersion,
    sources,
    mappings,
    currentSourceValue: sourceValue,
    currentTargetValue: targetValue,
  });

  return (
    <AlgorithmTargetMapTypeSourceField
      // `key` is needed for react-hook-form internal state to sync properly
      key={index}
      index={index}
      sources={sources}
      mappings={mappings}
      options={sourceOptions}
      areDbSources={areDbSources}
    />
  );
};
